function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

export function hexToOklch(hex: string): [number, number, number] {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const num = parseInt(h, 16);
  if (Number.isNaN(num)) return [0, 0, 0];
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const bb = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

  const C = Math.sqrt(a * a + bb * bb);
  let H = (Math.atan2(bb, a) * 180) / Math.PI;
  if (H < 0) H += 360;

  return [L, C, H];
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function setScale(name: string, L: number, C: number, H: number) {
  const root = document.documentElement;
  const r = (n: number) => n.toFixed(4);
  root.style.setProperty(`--${name}-500`, `${r(L)} ${r(C)} ${r(H)}`);
  root.style.setProperty(`--${name}-600`, `${r(clamp(L - 0.07, 0, 1))} ${r(C * 0.95)} ${r(H)}`);
  root.style.setProperty(`--${name}-700`, `${r(clamp(L - 0.15, 0, 1))} ${r(C * 0.9)} ${r(H)}`);
  root.style.setProperty(`--${name}-800`, `${r(clamp(L - 0.22, 0, 1))} ${r(C * 0.8)} ${r(H)}`);
  root.style.setProperty(`--${name}-900`, `${r(clamp(L - 0.3, 0, 1))} ${r(C * 0.6)} ${r(H)}`);
  root.style.setProperty(`--${name}-950`, `${r(clamp(L - 0.38, 0, 1))} ${r(C * 0.4)} ${r(H)}`);
  root.style.setProperty(`--${name}-400`, `${r(clamp(L + 0.08, 0, 1))} ${r(C)} ${r(H)}`);
  root.style.setProperty(`--${name}-300`, `${r(clamp(L + 0.2, 0, 1))} ${r(C * 0.5)} ${r(H)}`);
  root.style.setProperty(`--${name}-200`, `${r(clamp(L + 0.3, 0, 1))} ${r(C * 0.3)} ${r(H)}`);
  root.style.setProperty(`--${name}-100`, `${r(clamp(L + 0.37, 0, 1))} ${r(C * 0.2)} ${r(H)}`);
  root.style.setProperty(`--${name}-50`, `${r(clamp(L + 0.42, 0, 1))} ${r(C * 0.12)} ${r(H)}`);
}

function toBool(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v === "true" || v === "1";
  if (typeof v === "number") return v === 1;
  return true;
}

export function applyAppearance(settings: Record<string, unknown>) {
  const root = document.documentElement;

  const primary = settings.primary_color;
  const accent = settings.accent_color;
  const secondary = settings.secondary_color;
  const background = settings.background_color;

  if (typeof primary === "string" && primary.startsWith("#")) {
    const [L, C, H] = hexToOklch(primary);
    setScale("primary", L, C, H);
  }
  if (typeof accent === "string" && accent.startsWith("#")) {
    const [L, C, H] = hexToOklch(accent);
    setScale("accent", L, C, H);
  }
  if (typeof secondary === "string" && secondary.startsWith("#")) {
    const [L, C, H] = hexToOklch(secondary);
    setScale("secondary", L, C, H);
  }
  if (typeof background === "string" && background.startsWith("#")) {
    const [L, C, H] = hexToOklch(background);
    const r = (n: number) => n.toFixed(4);
    root.style.setProperty("--background-50", `${r(L)} ${r(C)} ${r(H)}`);
    root.style.setProperty("--background-100", `${r(clamp(L - 0.02, 0, 1))} ${r(C)} ${r(H)}`);
    root.style.setProperty("--background-200", `${r(clamp(L - 0.05, 0, 1))} ${r(C)} ${r(H)}`);
  }

  const radius = settings.border_radius;
  if (radius !== undefined && radius !== null && radius !== "") {
    root.style.setProperty("--radius-card", `${radius}px`);
  }

  root.classList.toggle("no-animations", !toBool(settings.animations_enabled));
  root.classList.toggle("no-fx", !toBool(settings.futuristic_enabled));
}

export function clearAppearance() {
  const root = document.documentElement;
  const names = ["primary", "accent", "secondary", "background"];
  names.forEach((name) => {
    for (let i = 1; i <= 9; i++) {
      root.style.removeProperty(`--${name}-${i * 100}`);
    }
    root.style.removeProperty(`--${name}-50`);
  });
  root.style.removeProperty("--radius-card");
  root.classList.remove("no-animations", "no-fx");
}